import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { useLocation } from "react-router-dom";

/** localStorage key; must match right ResizablePanel `storageKey` in CohesiveLayout. */
export const RIGHT_PANEL_WIDTH_STORAGE_KEY = "right-panel-width";

export function readStoredRightPanelWidth(): number {
  try {
    const raw = localStorage.getItem(RIGHT_PANEL_WIDTH_STORAGE_KEY);
    if (raw == null) return 0;
    const n = Number(JSON.parse(raw));
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export interface PaneLayoutWidthsContextValue {
  leftPanelWidth: number;
  setLeftPanelWidth: Dispatch<SetStateAction<number>>;
  rightPanelWidth: number;
  setRightPanelWidth: Dispatch<SetStateAction<number>>;
}

const defaultValue: PaneLayoutWidthsContextValue = {
  leftPanelWidth: 0,
  setLeftPanelWidth: () => {},
  rightPanelWidth: 0,
  setRightPanelWidth: () => {},
};

const PaneLayoutWidthsContext =
  createContext<PaneLayoutWidthsContextValue>(defaultValue);

export function usePaneLayoutWidths(): PaneLayoutWidthsContextValue {
  return useContext(PaneLayoutWidthsContext);
}

export function PaneLayoutWidthsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const location = useLocation();
  const isRightPanelOpen = !!location.state?.rightPane?.pathname;

  const [leftPanelWidth, setLeftPanelWidth] = useState(0);
  const [rightPanelWidth, setRightPanelWidth] = useState(0);

  useEffect(() => {
    if (isRightPanelOpen) {
      setRightPanelWidth((w) => (w > 0 ? w : readStoredRightPanelWidth()));
    } else {
      setRightPanelWidth(0);
    }
  }, [isRightPanelOpen]);

  const value = useMemo(
    () => ({
      leftPanelWidth,
      setLeftPanelWidth,
      rightPanelWidth,
      setRightPanelWidth,
    }),
    [leftPanelWidth, rightPanelWidth],
  );

  return (
    <PaneLayoutWidthsContext.Provider value={value}>
      {children}
    </PaneLayoutWidthsContext.Provider>
  );
}
